import React, { type FC, useEffect } from 'react';

import AvatarGroup, { type AvatarGroupProps } from '@atlaskit/avatar-group';

import { type ShowFlagArgs } from '@confluence/flags/entry-points/FlagsStateContainer.ts';

import { ResourceType } from '../resolveRestrictions/queries/__types__/BlockedAccessRestrictionsQuery.ts';
import type { RestrictedUserPrincipal } from '../commonTypes.tsx';

import type { AccessRequestsList } from './realtimeAccessRequestTypes.ts';

/**
 * Assumes requests are already filtered to only those
 * That have displayName and requestAccessType
 * */
export const makeFlagTitle = (requests: AccessRequestsList): string => {
	if (requests.length === 1) {
		const onlyRequest = requests[0];
		const name = onlyRequest?.user?.displayName!;
		const accessType = onlyRequest?.requestAccessType!;

		return `${name} wants to ${accessType?.toLowerCase()} this content`;
	}

	if (requests.length > 1) {
		const firstName = requests[0]?.user?.displayName!;

		if (requests.length === 2) {
			const secondName = requests[1]?.user?.displayName!;
			return `${firstName} and ${secondName} want to access your content`;
		}

		const remainingCount = requests.length - 1;
		return `${firstName} and ${remainingCount} others want to access your content`;
	}

	// Should not happen, because method gets invoked if there's at least one request
	return '';
};

export const makeFlagDescription = (
	requests: AccessRequestsList,
	isRbacEnabled: boolean,
	restrictedUserPrincipalInfo?: RestrictedUserPrincipal,
): string | null => {
	if (requests.length === 1 && restrictedUserPrincipalInfo) {
		const accessType = requests[0]?.requestAccessType!;

		const hasSpaceRestriction = restrictedUserPrincipalInfo.restrictedResources?.some(
			(resource) => resource?.resourceType === ResourceType.SPACE,
		);

		const hasParentRestriction = restrictedUserPrincipalInfo.restrictedResources?.some(
			(resource) => resource?.resourceType !== ResourceType.SPACE,
		);

		const parentRestrictionCount =
			restrictedUserPrincipalInfo.restrictedResources?.reduce((res, curr) => {
				const isParentRestriction = curr?.resourceType !== ResourceType.SPACE;
				return res + (isParentRestriction ? 1 : 0);
			}, 0) ?? 0;

		const spaceRole = isRbacEnabled
			? ` as a ${accessType === 'EDIT' ? 'collaborator' : 'viewer'}`
			: '';

		const items = parentRestrictionCount === 1 ? 'item' : 'items';

		if (hasSpaceRestriction && hasParentRestriction) {
			return `They'll need to be added to the space${spaceRole} and to ${parentRestrictionCount} parent ${items}.`;
		} else if (hasSpaceRestriction) {
			return `They'll need to be added to the space ${spaceRole}.`;
		} else if (hasParentRestriction) {
			return `They'll need to be added to ${parentRestrictionCount} parent ${items}.`;
		}
	}

	return null;
};

export const makeFlagCustomIcon = (requests: AccessRequestsList) => {
	const avatarData: AvatarGroupProps['data'] = requests
		.filter((request) => request?.user?.displayName && request?.requestAccessType)
		.map((request) => ({
			name: request?.user?.displayName!,
			src: request?.user?.photos?.find((photo) => photo?.isPrimary)?.value ?? undefined,
			key: request?.user?.id!,
		}));

	const maxCount = avatarData.length === 1 ? 1 : 2;
	return <AvatarGroup data={avatarData} maxCount={maxCount} />;
};

export const makeFlagActions = (
	requests: AccessRequestsList,
	resolveRestrictions: boolean,
	processRequest: (
		request: AccessRequestsList[number],
		decision: 'APPROVE' | 'DENY',
		resolveRestrictions: boolean,
	) => void,
	onReview: () => void,
	onViewDetails: () => void,
	hideFlag: () => void,
): ShowFlagArgs['actions'] => {
	if (requests.length === 1) {
		const approveRequest = () => {
			processRequest(requests[0], 'APPROVE', resolveRestrictions);
			hideFlag();
		};

		const denyRequest = () => {
			processRequest(requests[0], 'DENY', false);
			hideFlag();
		};

		const primaryAction = {
			content: resolveRestrictions ? 'Add to all' : 'Approve',
			onClick: approveRequest,
		};

		const secondaryAction = resolveRestrictions
			? {
					content: 'View details',
					onClick: onViewDetails,
				}
			: {
					content: 'Deny',
					onClick: denyRequest,
				};

		return [primaryAction, secondaryAction];
	}

	if (requests.length > 1) {
		return [{ content: 'Review requests', onClick: onReview }];
	}

	return [];
};

export const FlagRemovalDetector: FC<{ onDestroy: () => void; children: React.ReactNode }> = ({
	onDestroy,
	children,
}) => {
	useEffect(() => onDestroy, [onDestroy]);
	return children;
};
